import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Shield, 
  Crown,
  Save,
  CheckCircle,
  Calendar,
  MapPin
} from 'lucide-react';

const ProfilePage = () => {
  const { user, token, logout } = useAuth();
  const { getTierInfo } = useSubscription();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const tierInfo = getTierInfo();
  
  // Form state (read-only for now, could be expanded)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    location: user?.location || 'Not set'
  });

  const handleSave = async () => {
    setSaving(true);
    // Simulate save - in production this would call an API
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Dashboard</span>
            </Link>
          </div>
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_presence-check-26/artifacts/zsrnp3ib_veriwatch_dark.png"
              alt="VeriWatch"
              className="h-8 w-auto"
            />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Your Profile</h1>
          <p className="text-slate-400">Manage your account settings and preferences</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Profile Card */}
          <div className="md:col-span-1">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="profile-card">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-white">
                    {user.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-white mb-1">{user.name}</h2>
                <p className="text-sm text-slate-400 mb-3">{user.email}</p>
                <Badge 
                  className="text-white text-xs"
                  style={{ backgroundColor: tierInfo.color === 'teal' ? '#0d9488' : tierInfo.color === 'amber' ? '#d97706' : tierInfo.color === 'orange' ? '#ea580c' : '#475569' }}
                >
                  <Crown className="w-3 h-3 mr-1" />
                  {tierInfo.displayName}
                </Badge>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-800 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Calendar className="w-4 h-4" />
                  <span>Member since Dec 2024</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Account verified</span>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Form */}
          <div className="md:col-span-2 space-y-6">
            {/* Account Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="account-settings">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-teal-400" />
                Account Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-slate-300">Display Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="profile-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      disabled
                      className="mt-1 bg-slate-800/50 border-slate-700 text-slate-400 pr-20"
                      data-testid="profile-email-input"
                    />
                    <Badge className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 bg-green-900/50 text-green-400 text-[10px]">
                      Verified
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                </div>
                
                <div>
                  <Label htmlFor="location" className="text-slate-300">Default Location</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 mt-0.5 w-4 h-4 text-slate-500" />
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="Enter your city or ZIP code"
                      className="mt-1 bg-slate-800 border-slate-700 text-white pl-10"
                      data-testid="profile-location-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Used for local alerts and scanner recommendations</p>
                </div>
              </div>
              
              <div className="mt-6 flex items-center gap-3">
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-teal-600 hover:bg-teal-500"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : saved ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Subscription Info */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="subscription-info">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Subscription
              </h3>
              
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">{tierInfo.displayName} Plan</p>
                  <p className="text-sm text-slate-400">{tierInfo.description || 'Full access to VeriWatch features'}</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  onClick={() => navigate('/subscription')}
                  data-testid="manage-subscription-btn"
                >
                  Manage
                </Button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 justify-start"
                  onClick={() => navigate('/frequencies')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Frequency Database
                </Button>
                <Button 
                  variant="outline" 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 justify-start"
                  onClick={() => navigate('/pricing')}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
